import type { ReactNode } from 'react';
import type dayjs from 'dayjs';
import { get, type FieldErrors } from 'react-hook-form';

import type { DateValueFormat, FieldConfig } from '../config';
import type { SectionKey } from '../lease-details.types';
import { DateFormat } from '@/shared/libs/time.lib';

const DEFAULT_LEASE_DURATION_MONTHS = 12;

// Formats a linked date for form storage per `valueFormat`: `calendar` → `YYYY-MM-DD` ('' when empty),
// `iso` → ISO string (null when empty). Shared by the date field and its linked-date hook.
export const formatStoredDate = (
  date: dayjs.Dayjs | null,
  valueFormat: DateValueFormat = 'iso',
): string | null => {
  if (!date) {
    return valueFormat === 'calendar' ? '' : null;
  }
  return valueFormat === 'calendar' ? date.format(DateFormat.Calendar) : date.toISOString();
};

/** The default lease term from a start date: `start + months - 1 day` (12 months unless set). */
export const deriveLeaseEnd = (start: dayjs.Dayjs, months = DEFAULT_LEASE_DURATION_MONTHS) =>
  start.add(months, 'month').subtract(1, 'day');

export const buildPath = (sectionKey: SectionKey, name: string, arrayIndex?: number): string =>
  arrayIndex == null ? name : `${sectionKey}.${arrayIndex}.${name}`;

// Resolves a label that may be a static string or an index-aware callback.
// Switch/Checkbox kinds use `ReactNode` labels and never pass functions; this
// only takes effect when a string-kind field carries a callback label.
export const resolveFieldLabel = (
  label: FieldConfig['label'],
  index: number | undefined,
): ReactNode => {
  if (typeof label === 'function') {
    return label({ index });
  }
  return label;
};

export const resolveFieldHelperText = (
  helperText: FieldConfig['helperText'],
  index: number | undefined,
): ReactNode => {
  if (typeof helperText === 'function') {
    return (helperText as (ctx: { index?: number }) => ReactNode)({ index });
  }
  return helperText;
};

export const resolveFieldErrorHelperText = (
  errorHelperText: FieldConfig['errorHelperText'],
  index: number | undefined,
): ReactNode => {
  if (typeof errorHelperText === 'function') {
    return (errorHelperText as (ctx: { index?: number }) => ReactNode)({ index });
  }
  return errorHelperText;
};

export const firstErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const node = error as { message?: unknown };
  // Skip empty-string messages and recurse — RHF can produce
  // `{ type: 'submit', message: '' }` for programmatic errors with no copy,
  // and bubbling that '' would shadow a real message from a sibling/child.
  if (typeof node.message === 'string' && node.message) {
    return node.message;
  }
  for (const key of Object.keys(error)) {
    const found = firstErrorMessage((error as Record<string, unknown>)[key]);
    if (found) {
      return found;
    }
  }
  return undefined;
};

// Reads a dotted path (e.g. `applicants.0.email`) out of an RHF errors tree.
export const getNestedError = (errors: FieldErrors, path: string): unknown => get(errors, path);
